import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Interactive playground for AdMedia brand tracking URLs.
 *
 * Lets a publisher plug in sample values for the three pub-controlled
 * macros (`q`, `clickid`, `ref`), hit Preview, and see:
 *   1. The exact URL we'd 302-redirect them to (with macros substituted).
 *   2. A plain-English breakdown of how attribution flows back.
 *   3. The copy-paste tracker URL ready for a real test click.
 *
 * Calls POST /publisher/brands/{brand_id}/preview-tracker — a pure
 * simulation, NO click_log row is created.
 *
 * Macro inputs only render when the brand actually declares the macro
 * in `tracker_macros` — e.g. CPA-only AdMedia brands won't get a `q`
 * field at all.
 */
export default function TryTrackerDialog({ brand, onClose }) {
  const [q, setQ] = useState("");
  const [clickid, setClickid] = useState("");
  const [ref, setRef] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const macros = brand.tracker_macros || {};

  const handlePreview = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.post(
        `/publisher/brands/${brand.brand_id}/preview-tracker`,
        { q, clickid, ref },
      );
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="try-tracker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-violet-700">
              AdMedia · Try this URL
            </span>
          </DialogTitle>
          <DialogDescription>
            Plug in sample values for <code className="font-mono text-violet-700">q</code>,{" "}
            <code className="font-mono text-violet-700">clickid</code>, and{" "}
            <code className="font-mono text-violet-700">ref</code> to see exactly what URL
            we'd send to {brand.advertiser_name || brand.brand_name} and how we'd attribute
            the conversion back to you. <b>No click is logged</b> — pure preview.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {("q" in macros) && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                q <span className="font-normal normal-case text-slate-400">— {macros.q}</span>
              </Label>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. 65 inch oled tv"
                className="h-9 text-sm mt-1"
                data-testid="try-tracker-q"
              />
            </div>
          )}
          {("clickid" in macros) && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                clickid <span className="font-normal normal-case text-slate-400">— {macros.clickid}</span>
              </Label>
              <Input
                value={clickid}
                onChange={(e) => setClickid(e.target.value)}
                placeholder="e.g. PUB-CLK-12345"
                className="h-9 text-sm mt-1 font-mono"
                data-testid="try-tracker-clickid"
              />
            </div>
          )}
          {("ref" in macros) && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                ref <span className="font-normal normal-case text-slate-400">— {macros.ref}</span>
              </Label>
              <Input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="leave blank to fall back to your sub_id1"
                className="h-9 text-sm mt-1 font-mono"
                data-testid="try-tracker-ref"
              />
            </div>
          )}

          <Button
            onClick={handlePreview}
            disabled={submitting}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="try-tracker-submit"
          >
            {submitting ? "Building preview…" : "Preview redirect"}
          </Button>
        </div>

        {result && (
          <div className="mt-3 space-y-3 border-t border-slate-200 pt-4" data-testid="try-tracker-result">
            {/* Tracker URL — copy-paste-able */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                Your tracker URL (paste into a browser to actually test)
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-[11px] text-slate-700 break-all">
                  {result.tracker_url}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copy(result.tracker_url, "Tracker URL")}
                  className="shrink-0"
                  data-testid="try-tracker-copy-url"
                >
                  <Copy size={12} />
                </Button>
              </div>
            </div>

            {/* Final redirect URL */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
                We'll 302-redirect to AdMedia at
              </div>
              <code className="block bg-emerald-50/60 border border-emerald-200 rounded px-3 py-2 font-mono text-[11px] text-emerald-900 break-all">
                {result.redirect_url}
              </code>
            </div>

            {/* Attribution breakdown */}
            <div className="border border-violet-200 bg-violet-50/40 rounded px-3 py-3 text-[11px]">
              <div className="font-bold uppercase tracking-[0.2em] text-violet-700 text-[10px] mb-2">
                How attribution will flow back
              </div>
              <ul className="space-y-1.5 text-slate-700">
                <li>
                  <span className="text-slate-500">Your sub_id:</span>{" "}
                  <span className="font-mono font-semibold">{result.attribution.publisher_sub_id}</span>
                </li>
                <li className="leading-relaxed">{result.attribution.how_we_attribute}</li>
                <li>
                  <span className="text-slate-500">Your clickid back on report:</span>{" "}
                  <span className="font-mono">{result.attribution.your_clickid_on_csv}</span>
                </li>
                <li>
                  <span className="text-slate-500">ref value sent to AdMedia:</span>{" "}
                  <span className="font-mono">{result.attribution.ref_value_sent}</span>
                  {result.attribution.ref_was_fallback && (
                    <span className="text-amber-700 italic"> (auto-filled from your sub_id1)</span>
                  )}
                </li>
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
